import {
  DotsThreeVertical,
  Eye,
  EyeSlash,
  Flag,
  Info,
  PaperPlaneTilt,
  Phone,
  Prohibit,
  Star,
  UserMinus,
  UserPlus,
  X,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { FriendshipStatus, OrganizerRating } from '@sirocco/shared';
import { events, messages as messagesApi, moderation, users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button, IconButton } from '../../components/ui/Button';
import { EmptyState, BadgeOfficiel } from '../../components/ui/Bits';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { reportPath } from '../report/ReportScreen';
import { useCall } from '../../store/CallContext';
import { useFlashOnArrival } from '../../utils/flash';
import { clearScreenCache, useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { locale, t } from '../../i18n';
import { GestesDuMembre, useGestesDuMembre, useMonRole } from '../moderation/Gestes';

/**
 * Libelle du bouton d'amitie, selon ou en est la relation.
 *
 * Une fois amis, il propose de defaire plutot que de constater : « Amis »
 * n'etait qu'une etiquette, et le bouton ne repondait a rien. Ce qu'on
 * peut encore faire depuis un profil ami, c'est en sortir.
 */
const FRIEND_LABELS: Record<FriendshipStatus, string> = {
  none: t.profil.membre.ami.none,
  requested: t.profil.membre.ami.requested,
  incoming: t.profil.membre.ami.incoming,
  friends: t.profil.membre.ami.friends,
};

/**
 * Profil public d'un autre membre.
 *
 * C'est la couche de donnees qui applique les reglages de confidentialite :
 * quand elle renvoie `restricted`, l'ecran n'a rien a masquer lui-meme, il
 * n'a simplement rien recu.
 */
export function MemberProfileScreen() {
  const { userId = '' } = useParams();
  const { user } = useSession();

  /**
   * Sa note d'organisateur.
   *
   * Lue a part du profil : elle ne concerne que ceux qui organisent, et
   * la poser sur le type d'un membre ferait payer une lecture a tous les
   * ecrans qui en affichent un.
   */
  const [noteOrga, setNoteOrga] = useState<OrganizerRating | null>(null);
  useEffect(() => {
    if (!userId) return;
    let vivant = true;
    void events
      .organizerRating(userId)
      .then((note) => vivant && setNoteOrga(note))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [userId]);
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(
    () => usersApi.publicProfile(userId),
    [userId],
    `member:${userId}`,
  );

  const [menuOpen, setMenuOpen] = useState(false);
  /* Le bouton d'en-tete d'ou le menu sort. */
  const menuAnchor = useRef<HTMLButtonElement>(null);
  const [toast, setToast] = useState<string | null>(null);
  /* Le retour d'un ecran de signalement, avec son accuse de reception. */
  useFlashOnArrival(setToast);
  const [busy, setBusy] = useState(false);
  // Refuser, retirer, bloquer : trois gestes sans retour, tous confirmes.
  const { pending, request, cancel, confirm } = useConfirm();
  const { appeler, occupe } = useCall();

  const status = data?.friendshipStatus ?? 'none';
  const soft = status === 'requested' || status === 'friends';
  /*
    Le compte de l'application.

    Il n'est pas un membre parmi les autres : pas de destinations ni de
    trophees — il ne joue pas au jeu qu'il propose —, pas d'amitie, pas
    d'appel, pas de signalement. Il reste une chose a en faire, et c'est
    la seule qui compte : lui ecrire.
  */
  const officiel = data?.user.officiel === true;
  /*
    Sa propre fiche.

    On y arrive depuis la liste des participants d'une sortie : c'est la
    meme page que pour les autres, et c'est ce qu'on veut. Mais rien de
    ce qu'elle propose ne s'adresse a soi — on ne s'ajoute pas en ami,
    on ne s'ecrit pas, on ne s'appelle pas, on ne se signale pas. Les
    actions disparaissent donc au lieu d'etre grisees : un bouton mort
    se retente, et rien a l'ecran ne dirait pourquoi il ne repond pas.
  */
  const cestMoi = Boolean(user && data && user.id === data.user.id);

  /*
    L'administration, sous la fiche.

    Pour l'administrateur seul, et jamais sur soi ni sur le compte de
    l'application : la fonction refuserait l'un, et l'autre n'a rien a
    subir. On arrive ici depuis la liste de tous les comptes, et l'on
    agit sans passer par la recherche de l'ecran d'administration.
  */
  const monRole = useMonRole();
  const administre = monRole === 'admin' && Boolean(data) && !cestMoi && !officiel;

  /*
    Son adresse electronique, sous son pseudo : pour le proprietaire de
    l'application seul, et c'est la fonction qui en juge. On ne la
    demande qu'avec un role d'administrateur, pour que l'ouverture d'un
    profil ne coute pas un appel a chaque membre.
  */
  const demandeLAdresse = monRole === 'admin' && Boolean(userId) && userId !== user?.id;
  const { data: adresse } = useAsync(
    () => (demandeLAdresse ? moderation.adresse(userId) : Promise.resolve(null)),
    [demandeLAdresse, userId],
    demandeLAdresse ? `adresse:${userId}` : undefined,
  );
  const outils = useGestesDuMembre(administre && data ? data.user : null, {
    // La coche posee ou retiree se voit a cote du pseudo.
    apresGeste: reload,
    // Le compte n'est plus : les listes gardees en memoire le portent encore.
    apresSuppression: () => {
      clearScreenCache();
      navigate(-1);
    },
  });

  function closeMenu() {
    setMenuOpen(false);
  }

  /**
   * Referme la feuille, puis pose la question au centre de l'ecran.
   * Les deux couches ne cohabitent pas : la question doit etre la seule
   * chose a l'ecran au moment ou on y repond.
   */
  function ask(question: string, run: () => void) {
    closeMenu();
    request(question, run);
  }

  /**
   * Petit message ephemere, comme dans le feed. Il reste le temps de le
   * lire : celui d'un blocage dit tout ce qui change, en deux phrases.
   */
  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), Math.max(2600, message.length * 45));
  }

  /**
   * Le bouton principal fait ce que son libelle annonce.
   * "Accepter la demande" doit accepter : envoyer une demande en retour
   * laisserait deux demandes croisees et aucune amitie.
   */
  async function onFriend() {
    setBusy(true);
    try {
      if (status === 'incoming') await usersApi.acceptRequest(userId);
      else if (status !== 'friends') await usersApi.requestFriend(userId);
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setBusy(false);
      reload();
    }
  }

  /** Chaque action de la feuille suit le meme chemin : agir, dire, recharger. */
  async function run(action: () => Promise<unknown>, message: string) {
    closeMenu();
    setBusy(true);
    try {
      await action();
      flash(message);
    } catch (cause) {
      flash(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setBusy(false);
      reload();
    }
  }

  async function openConversation() {
    try {
      const conversation = await messagesApi.openDirect(userId);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      /*
        Le refus se dit. Il etait avale ici « pour ne pas casser
        l'affichage du profil » : le bouton paraissait alors mort, et il
        n'y avait aucun moyen de savoir pourquoi.
      */
      flash(cause instanceof Error ? cause.message : t.sorties.fiche.ecrireImpossible);
    }
  }

  const username = data?.user.username ?? t.profil.membre.ceMembre;

  return (
    <SubScreen
      title={data?.user.username ?? t.profil.membre.profil}
      action={
        data && (
          <IconButton
            label={t.sorties.commun.autresActions}
            variant="ghost"
            size={34}
            ref={menuAnchor}
            onClick={() => setMenuOpen(true)}
          >
            <DotsThreeVertical size={22} weight="bold" />
          </IconButton>
        )
      }
      overlay={
        data && (
          <>
            {/* Sans titre : on vient d'appuyer sur les trois points de ce
                membre-la, redire son nom n'ajoute rien. */}
            <Sheet open={menuOpen} anchor={menuAnchor} onClose={closeMenu}>
              {status === 'incoming' && (
                <SheetAction
                  icon={<X size={22} />}
                  onClick={() =>
                    ask(
                      t.profil.membre.refuserQ,
                      () =>
                        void run(
                          () => usersApi.declineRequest(userId),
                          t.profil.membre.demandeRefusee(username),
                        ),
                    )
                  }
                >
                  {t.profil.membre.refuser}
                </SheetAction>
              )}

              {/*
                Masquer, avant bloquer : le geste doux d'abord. On cesse de
                voir ses publications et ses sorties, sans rien rompre ni
                rien lui dire — d'ou l'absence de question : cela se defait
                d'un appui, ici meme. Sans objet chez soi, ni sur un compte
                deja bloque, dont on ne voit deja plus rien.
              */}
              {!cestMoi &&
                !data.blocked &&
                (data.hidden ? (
                  <SheetAction
                    icon={<Eye size={22} />}
                    onClick={() =>
                      void run(
                        () => usersApi.unhide(userId).then(clearScreenCache),
                        t.profil.membre.plusMasque(username),
                      )
                    }
                  >
                    {t.profil.membre.nePlusMasquer}
                  </SheetAction>
                ) : (
                  <SheetAction
                    icon={<EyeSlash size={22} />}
                    onClick={() =>
                      /*
                        Le fil et les sorties gardes en memoire portent encore
                        ses publications : on les oublie, ils se reliront sans
                        lui. Sans cela il restait a l'ecran jusqu'au prochain
                        tirage, et le geste semblait n'avoir rien fait.
                      */
                      void run(
                        () => usersApi.hide(userId).then(clearScreenCache),
                        t.profil.membre.estMasque(username),
                      )
                    }
                  >
                    {t.profil.membre.masquerCompte}
                  </SheetAction>
                ))}

              {data.blocked ? (
                <SheetAction
                  icon={<Prohibit size={22} />}
                  onClick={() =>
                    void run(() => usersApi.unblock(userId), t.profil.membre.debloque(username))
                  }
                >
                  {t.profil.membre.debloquerMembre}
                </SheetAction>
              ) : (
                <SheetAction
                  icon={<Prohibit size={22} />}
                  tone="danger"
                  onClick={() =>
                    ask(
                      t.profil.membre.bloquerQ(username),
                      () =>
                        void run(
                          () => usersApi.block(userId),
                          t.profil.membre.bloque(username),
                        ),
                    )
                  }
                >
                  {t.profil.membre.bloquerMembre}
                </SheetAction>
              )}

              {/* Le motif et le detail se choisissent sur leur propre ecran. */}
              {!officiel && (
                <SheetAction
                  icon={<Flag size={22} />}
                  onClick={() => navigate(reportPath('member', userId))}
                >
                  {t.fil.commun.signaler}
                </SheetAction>
              )}
            </Sheet>

            <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />

            {outils.fenetre}

            {toast && <p className="feed__toast">{toast}</p>}
          </>
        )
      }
    >
      {/* Le profil est en cache : y revenir le reaffiche tout de suite,
          et la relecture se fait en silence. Le tourniquet ne sert donc
          qu'a la toute premiere ouverture. */}
      {loading && !data && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <>
          <div className={`pr-member${data.user.bio.trim() ? '' : ' pr-member--sans-bio'}`}>
            <Avatar user={data.user} size={106} ring />
            <div className="pr-member__identity">
              <span className="pr-member__name">{data.user.username}</span>
              {data.user.badge && (
                <span className="drapeau pr-badge" aria-hidden="true">
                  {data.user.badge}
                </span>
              )}
              {/* La coche du compte de l'application, et rien pour les
                  autres. */}
              {(data.user.officiel || data.user.verifie) && (
                <BadgeOfficiel size="sm" application={data.user.officiel} />
              )}
            </div>
            {adresse && <p className="pr-member__adresse">{adresse}</p>}
            {/* La note d'organisateur, sous le pseudo : c'est ce qu'on
                vient verifier avant de s'inscrire chez quelqu'un. */}
            {noteOrga && (
              <span
                className="pr-orga"
                aria-label={t.profil.commun.notePar(noteOrga.moyenne.toLocaleString(locale), noteOrga.nombre)}
              >
                <Star size={15} weight="fill" />
                {noteOrga.moyenne.toLocaleString(locale)}
                {/*
                  Au-dela de cinq cents, le compte cesse de compter.

                  Un nombre exact n'apprend plus rien a ce niveau — on sait
                  deja qu'on a affaire a quelqu'un qui organise — et il
                  s'allongerait sans fin sous un pseudo.
                */}
                <span className="pr-orga__compte">
                  {t.profil.membre.avis(noteOrga.nombre > 500 ? '+ 500' : String(noteOrga.nombre))}
                </span>
              </span>
            )}

            {/*
              Rien du tout quand il n'y a pas de bio.

              Le paragraphe etait pose sans condition : vide, il gardait
              sa hauteur de ligne et l'ecart de la colonne, et laissait
              trente-quatre pixels de blanc entre le pseudo et les
              actions. On croyait a un oubli de mise en page. Mon propre
              profil posait deja cette garde ; celui d'un membre l'avait
              perdue en route.
            */}
            {data.user.bio.trim() && <p className="pr-member__bio">{data.user.bio}</p>}
          </div>

          {/* Chez soi, rien a faire : voir `cestMoi`. */}
          {cestMoi ? null : /* Un membre bloque n'appelle plus qu'une seule action : le debloquer. */
          data.blocked ? (
            <div className="pr-actions">
              <Button
                variant="soft"
                icon={<Prohibit size={20} />}
                disabled={busy}
                onClick={() =>
                  void run(() => usersApi.unblock(userId), t.profil.membre.debloque(username))
                }
              >
                {t.profil.commun.debloquer}
              </Button>
            </div>
          ) : officiel ? (
            /*
              Une seule action, et elle est ouverte a tout le monde.

              Le bouton d'amitie et le combine ne sont pas grises : ils
              n'existent pas. Un bouton mort se retente, et rien a
              l'ecran ne dirait pourquoi il ne repond pas.
            */
            <div className="pr-actions">
              <Button icon={<PaperPlaneTilt className="sens-x" size={20} />} onClick={() => void openConversation()}>
                {t.profil.membre.ecrire}
              </Button>
            </div>
          ) : (
            <div className="pr-actions">
              <Button
                variant={soft ? 'soft' : 'primary'}
                icon={status === 'friends' ? <UserMinus size={20} /> : <UserPlus size={20} />}
                disabled={busy}
                onClick={() =>
                  // Defaire une amitie ne se fait pas d'un doigt qui derape.
                  status === 'friends'
                    ? ask(
                        t.profil.membre.retirerAmiQ(username),
                        () =>
                          void run(
                            () => usersApi.removeFriend(userId),
                            t.profil.membre.plusAmi(username),
                          ),
                      )
                    : void onFriend()
                }
              >
                {FRIEND_LABELS[status]}
              </Button>
              {/*
                Ecrire est ouvert a tous. Entre amis, cela mene a la
                conversation ; sinon cela ouvre une demande — un message,
                un seul, qui attend une reponse dans l'ecran des demandes.
                Meme hauteur que le bouton d'amitie, pour une ligne d'un
                seul tenant.
              */}
              <IconButton label={t.profil.membre.ecrire} size={44} onClick={() => void openConversation()}>
                <PaperPlaneTilt className="sens-x" size={20} />
              </IconButton>
              {/*
                Appeler suit la meme regle qu'ecrire, et pour une raison de
                plus : le telephone de l'autre se met a sonner. Ca ne se
                donne pas au premier profil venu.
              */}
              {status === 'friends' && (
                <IconButton
                  label={t.profil.membre.appeler(username)}
                  size={44}
                  disabled={occupe}
                  onClick={() =>
                    void appeler({
                      id: userId,
                      username,
                      avatarUrl: data.user.avatarUrl,
                    })
                  }
                >
                  <Phone size={20} />
                </IconButton>
              )}
            </div>
          )}

          {data.blocked ? (
            <EmptyState
              title={t.profil.membre.tuAsBloque}
              hint={t.profil.membre.tuAsBloqueAide}
            />
          ) : officiel ? (
            /*
              Ni « Ses destinations » ni « Ses trophees » : deux titres
              suivis de rien se lisent comme un chargement en panne. Une
              ligne dit ce qu'est ce compte, et c'est tout le profil.
            */
            <p className="pr-note">
              <Info size={20} />
              {t.profil.membre.officiel}
            </p>
          ) : data.restricted ? (
            <EmptyState
              title={t.profil.membre.reserve}
              hint={t.profil.membre.reserveAide}
            />
          ) : (
            <>
              {/*
                Pas de « Voir tout » ici : la liste est deja entiere. Le
                lien menait vers mes propres destinations, et il promettait
                une suite qui n'existe pas — c'est la couche donnee qui
                decide ce qu'un profil laisse voir, selon les reglages de
                son proprietaire, et elle envoie tout ce qui est permis.
              */}
              <h3 className="pr-heading">{t.profil.membre.sesDestinations}</h3>
              <div className="card-list">
                {data.destinations.map((destination) => (
                  <div key={destination.serverId} className="pr-dest-row">
                    <span className="pr-dest-row__name">{destination.serverName}</span>
                  </div>
                ))}
              </div>


              {data.eventsHidden && (
                <p className="pr-note">
                  <Info size={20} />
                  {t.profil.membre.sesEvenementsMasques}
                </p>
              )}
            </>
          )}

          {administre && outils.dossier && (
            <section className="pr-admin">
              <h3 className="pr-heading">{t.moderation.admin.titre}</h3>
              <GestesDuMembre outils={outils} avecEtat />
            </section>
          )}
        </>
      )}
    </SubScreen>
  );
}
